import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type {
  TournamentLobby,
  TournamentParticipant,
  TournamentSettings,
} from '../types/tournament';
import { groupService } from './GroupService';

export class TournamentLobbyService {
  private readonly LOBBIES_COLLECTION = 'tournamentLobbies';
  private readonly USERS_COLLECTION = 'users';

  /**
   * Fetch user avatar data from Firestore
   * Filters out undefined values to avoid Firestore errors
   */
  private async getUserAvatarData(userId: string) {
    const userDoc = await getDoc(doc(db, this.USERS_COLLECTION, userId));
    if (!userDoc.exists()) {
      return {};
    }
    
    const userData = userDoc.data();
    const avatarData: any = {};
    
    // Only include fields that are defined
    if (userData.photoURL !== undefined) avatarData.photoURL = userData.photoURL;
    if (userData.customAvatarType !== undefined) avatarData.customAvatarType = userData.customAvatarType;
    if (userData.customAvatarValue !== undefined) avatarData.customAvatarValue = userData.customAvatarValue;
    if (userData.avatarBackgroundType !== undefined) avatarData.avatarBackgroundType = userData.avatarBackgroundType;
    if (userData.avatarBackgroundValue !== undefined) avatarData.avatarBackgroundValue = userData.avatarBackgroundValue;
    
    return avatarData;
  }

  /**
   * Create a new tournament lobby
   * Automatically leaves any other active lobbies (one lobby at a time rule)
   */
  async createLobby(
    hostUserId: string,
    hostDisplayName: string,
    hostPhotoURL: string | undefined,
    groupId?: string,
    isDemo?: boolean
  ): Promise<string> {
    // FIRST: Leave all other lobbies where user is a participant (not host)
    const otherLobbies = await this.getJoinedLobbies(hostUserId);
    
    if (otherLobbies.length > 0) {
      await Promise.all(
        otherLobbies.map((otherLobby) => 
          this.leaveLobby(otherLobby.id, hostUserId).catch((err) => {
            console.error(`Failed to leave lobby ${otherLobby.id}:`, err);
          })
        )
      );
    }

    // Also close any lobbies where user is the host (can only host one at a time)
    const myOldLobbies = await this.getMyLobbies(hostUserId);
    if (myOldLobbies.length > 0) {
      await Promise.all(
        myOldLobbies.map((oldLobby) => 
          this.closeLobby(oldLobby.id, hostUserId).catch((err) => {
            console.error(`Failed to close lobby ${oldLobby.id}:`, err);
          })
        )
      );
    }

    const lobbyRef = doc(collection(db, this.LOBBIES_COLLECTION));
    const lobbyId = lobbyRef.id;

    let participants: TournamentParticipant[] = [];
    let isPersistent = false;

    if (groupId) {
      // Load group members as participants
      const group = await groupService.getGroup(groupId);
      if (!group) {
        throw new Error('Group not found');
      }

      // Fetch avatar data for each member
      const participantsWithAvatars = await Promise.all(
        group.members.map(async (member) => {
          const avatarData = await this.getUserAvatarData(member.userId);
          return {
            userId: member.userId,
            displayName: member.displayName,
            ...avatarData,
            status: member.userId === hostUserId ? 'joined' : 'invited',
            joinedAt: member.userId === hostUserId ? new Date() : undefined,
          } as TournamentParticipant;
        })
      );
      participants = participantsWithAvatars;

      isPersistent = true;
    } else {
      // Quick match - fetch host avatar data
      const avatarData = await this.getUserAvatarData(hostUserId);
      participants = [
        {
          userId: hostUserId,
          displayName: hostDisplayName,
          ...avatarData,
          status: 'joined',
          joinedAt: new Date(),
        },
      ];

      // For demo mode, automatically add bot player
      if (isDemo) {
        participants.push({
          userId: 'bot_player_demo',
          displayName: 'Demo Bot',
          customAvatarType: 'preset',
          customAvatarValue: 'robot',
          avatarBackgroundType: 'solid',
          avatarBackgroundValue: 'blue',
          status: 'joined',
          joinedAt: new Date(),
        });
      }
    }

    // Default settings
    const defaultSettings: TournamentSettings = {
      errorMode: 'strict',
      contentType: 'text',
      category: 'words',
      levelConstraints: {
        minDifficulty: 1,
        maxDifficulty: 6,
      },
      totalRounds: isDemo ? 3 : 6, // Demo lobbies default to 3 rounds for faster test loops
    };

    // Build ready approvals - host is always ready, bot is ready in demo mode
    const readyApprovals: { [userId: string]: boolean } = {
      [hostUserId]: true, // Host is automatically ready
    };
    
    if (isDemo) {
      readyApprovals['bot_player_demo'] = true; // Bot is automatically ready in demo mode
    }

    // For demo mode, check if all participants are ready
    const joinedParticipants = participants.filter((p) => p.status === 'joined');
    const allReady = joinedParticipants.every((p) => readyApprovals[p.userId] === true);
    const initialStatus = allReady ? 'ready' : 'waiting';

    console.log('🎮 Demo lobby creation:', {
      isDemo,
      participantsCount: participants.length,
      joinedCount: joinedParticipants.length,
      readyApprovals,
      allReady,
      initialStatus,
      participantUserIds: participants.map(p => p.userId),
    });

    const lobby: Omit<TournamentLobby, 'id'> = {
      hostUserId,
      groupId,
      isPersistent,
      isDemo: isDemo || false,
      status: initialStatus,
      createdAt: new Date(),
      participants,
      settings: defaultSettings,
      readyApprovals,
    };

    // Prepare data for Firestore (use null instead of undefined)
    const lobbyData: any = {
      hostUserId,
      isPersistent,
      isDemo: isDemo || false,
      status: initialStatus, // Use computed status (ready if all participants are ready)
      createdAt: Timestamp.fromDate(lobby.createdAt),
      participants: participants.map((p) => {
        return {
          userId: p.userId,
          displayName: p.displayName,
          status: p.status,
          joinedAt: p.joinedAt ? Timestamp.fromDate(p.joinedAt) : null,
          photoURL: p.photoURL || null,
          customAvatarType: p.customAvatarType || null,
          customAvatarValue: p.customAvatarValue || null,
          avatarBackgroundType: p.avatarBackgroundType || null,
          avatarBackgroundValue: p.avatarBackgroundValue || null,
        };
      }),
      settings: defaultSettings,
      readyApprovals: readyApprovals, // Use the prepared ready approvals (includes bot in demo mode)
    };

    // Only add groupId if it exists
    if (groupId) {
      lobbyData.groupId = groupId;
    }

    await setDoc(lobbyRef, lobbyData);

    return lobbyId;
  }

  /**
   * Get lobby by ID
   */
  async getLobby(lobbyId: string): Promise<TournamentLobby | null> {
    const lobbyDoc = await getDoc(doc(db, this.LOBBIES_COLLECTION, lobbyId));
    if (!lobbyDoc.exists()) {
      return null;
    }

    const data = lobbyDoc.data();
    return {
      id: lobbyDoc.id,
      hostUserId: data.hostUserId,
      groupId: data.groupId,
      isPersistent: data.isPersistent,
      status: data.status,
      tournamentId: data.tournamentId,
      createdAt: data.createdAt.toDate(),
      participants: data.participants.map((p: any) => ({
        userId: p.userId,
        displayName: p.displayName,
        photoURL: p.photoURL,
        customAvatarType: p.customAvatarType,
        customAvatarValue: p.customAvatarValue,
        avatarBackgroundType: p.avatarBackgroundType,
        avatarBackgroundValue: p.avatarBackgroundValue,
        status: p.status,
        joinedAt: p.joinedAt?.toDate(),
      })),
      settings: data.settings,
      readyApprovals: data.readyApprovals || {},
      settingSuggestions: data.settingSuggestions || [],
    };
  }

  /**
   * Refresh participant avatar data (fetch latest from Firestore)
   */
  async refreshParticipantAvatars(lobbyId: string): Promise<void> {
    const lobby = await this.getLobby(lobbyId);
    if (!lobby) {
      throw new Error('Lobby not found');
    }

    // Fetch latest avatar data for all participants
    const updatedParticipants = await Promise.all(
      lobby.participants.map(async (participant) => {
        const avatarData = await this.getUserAvatarData(participant.userId);
        return {
          ...participant,
          ...avatarData,
        };
      })
    );

    // Update lobby with fresh avatar data (use null for undefined)
    const participantsForFirestore = updatedParticipants.map((p) => {
      return {
        userId: p.userId,
        displayName: p.displayName,
        status: p.status,
        joinedAt: p.joinedAt ? Timestamp.fromDate(p.joinedAt) : null,
        photoURL: p.photoURL || null,
        customAvatarType: p.customAvatarType || null,
        customAvatarValue: p.customAvatarValue || null,
        avatarBackgroundType: p.avatarBackgroundType || null,
        avatarBackgroundValue: p.avatarBackgroundValue || null,
      };
    });

    await updateDoc(doc(db, this.LOBBIES_COLLECTION, lobbyId), {
      participants: participantsForFirestore,
    });
  }

  /**
   * Invite participant to lobby
   */
  async inviteToLobby(
    lobbyId: string,
    userId: string,
    displayName: string,
    photoURL: string | undefined
  ): Promise<void> {
    const lobby = await this.getLobby(lobbyId);
    if (!lobby) {
      throw new Error('Lobby not found');
    }

    // Check if already in lobby
    if (lobby.participants.some((p) => p.userId === userId)) {
      throw new Error('User is already in this lobby');
    }

    // Check max participants (10)
    if (lobby.participants.length >= 10) {
      throw new Error('Lobby is full');
    }

    // Fetch user's avatar data
    const avatarData = await this.getUserAvatarData(userId);

    const newParticipant: TournamentParticipant = {
      userId,
      displayName,
      ...avatarData,
      status: 'invited',
    };

    await updateDoc(doc(db, this.LOBBIES_COLLECTION, lobbyId), {
      participants: [
        ...lobby.participants.map((p) => ({
          userId: p.userId,
          displayName: p.displayName,
          status: p.status,
          joinedAt: p.joinedAt ? Timestamp.fromDate(p.joinedAt) : null,
          photoURL: p.photoURL || null,
          customAvatarType: p.customAvatarType || null,
          customAvatarValue: p.customAvatarValue || null,
          avatarBackgroundType: p.avatarBackgroundType || null,
          avatarBackgroundValue: p.avatarBackgroundValue || null,
        })),
        {
          userId: newParticipant.userId,
          displayName: newParticipant.displayName,
          photoURL: newParticipant.photoURL || null,
          customAvatarType: newParticipant.customAvatarType || null,
          customAvatarValue: newParticipant.customAvatarValue || null,
          avatarBackgroundType: newParticipant.avatarBackgroundType || null,
          avatarBackgroundValue: newParticipant.avatarBackgroundValue || null,
          status: newParticipant.status,
          joinedAt: null,
        },
      ],
    });
  }

  /**
   * Join lobby (for public Quick Match lobbies)
   * Automatically leaves any other active lobbies (one lobby at a time rule)
   */
  async joinLobby(
    lobbyId: string,
    userId: string,
    displayName: string,
    photoURL?: string
  ): Promise<void> {
    // FIRST: Leave all other lobbies where user is a participant (not host)
    const otherLobbies = await this.getJoinedLobbies(userId);
    const otherActiveLobbies = otherLobbies.filter((l) => l.id !== lobbyId);
    
    if (otherActiveLobbies.length > 0) {
      await Promise.all(
        otherActiveLobbies.map((otherLobby) => 
          this.leaveLobby(otherLobby.id, userId).catch((err) => {
            console.error(`Failed to leave lobby ${otherLobby.id}:`, err);
          })
        )
      );
    }

    const lobby = await this.getLobby(lobbyId);
    if (!lobby) {
      throw new Error('Lobby not found');
    }

    // Check if lobby is full
    if (lobby.participants.length >= 10) {
      throw new Error('Lobby is full (max 10 players)');
    }

    // Fetch user's avatar data
    const avatarData = await this.getUserAvatarData(userId);

    // Check if user already in lobby
    const existingParticipant = lobby.participants.find((p) => p.userId === userId);
    if (existingParticipant) {
      // Already joined, just update status and avatar
      existingParticipant.status = 'joined';
      existingParticipant.joinedAt = new Date();
      Object.assign(existingParticipant, avatarData);
    } else {
      // Add new participant
      lobby.participants.push({
        userId,
        displayName,
        ...avatarData,
        status: 'joined',
        joinedAt: new Date(),
      });
    }

    await updateDoc(doc(db, this.LOBBIES_COLLECTION, lobbyId), {
      participants: lobby.participants.map((p) => ({
        userId: p.userId,
        displayName: p.displayName,
        status: p.status,
        joinedAt: p.joinedAt ? Timestamp.fromDate(p.joinedAt) : null,
        photoURL: p.photoURL || null,
        customAvatarType: p.customAvatarType || null,
        customAvatarValue: p.customAvatarValue || null,
        avatarBackgroundType: p.avatarBackgroundType || null,
        avatarBackgroundValue: p.avatarBackgroundValue || null,
      })),
    });
  }

  /**
   * Leave lobby
   */
  async leaveLobby(lobbyId: string, userId: string): Promise<void> {
    const lobby = await this.getLobby(lobbyId);
    if (!lobby) {
      throw new Error('Lobby not found');
    }

    // If host leaves, delete lobby
    if (userId === lobby.hostUserId) {
      await deleteDoc(doc(db, this.LOBBIES_COLLECTION, lobbyId));
      return;
    }

    // Remove participant
    const updatedParticipants = lobby.participants.filter((p) => p.userId !== userId);

    await updateDoc(doc(db, this.LOBBIES_COLLECTION, lobbyId), {
      participants: updatedParticipants.map((p) => ({
        userId: p.userId,
        displayName: p.displayName,
        status: p.status,
        joinedAt: p.joinedAt ? Timestamp.fromDate(p.joinedAt) : null,
        photoURL: p.photoURL || null,
        customAvatarType: p.customAvatarType || null,
        customAvatarValue: p.customAvatarValue || null,
        avatarBackgroundType: p.avatarBackgroundType || null,
        avatarBackgroundValue: p.avatarBackgroundValue || null,
      })),
    });
  }

  /**
   * Update lobby settings (host only)
   */
  async updateSettings(
    lobbyId: string,
    userId: string,
    settings: Partial<TournamentSettings>
  ): Promise<void> {
    const lobby = await this.getLobby(lobbyId);
    if (!lobby) {
      throw new Error('Lobby not found');
    }

    if (userId !== lobby.hostUserId) {
      throw new Error('Only the host can update settings');
    }

    // Reset ready approvals when settings change (but keep host as ready)
    await updateDoc(doc(db, this.LOBBIES_COLLECTION, lobbyId), {
      settings: {
        ...lobby.settings,
        ...settings,
      },
      readyApprovals: {
        [lobby.hostUserId]: true, // Host remains ready
      },
      status: 'configuring',
    });
  }

  /**
   * Toggle demo mode (admin host only)
   * Demo mode uses simple test text for all levels to help debug multiplayer
   */
  async setDemoMode(
    lobbyId: string,
    userId: string,
    isDemo: boolean
  ): Promise<void> {
    const lobby = await this.getLobby(lobbyId);
    if (!lobby) {
      throw new Error('Lobby not found');
    }

    if (userId !== lobby.hostUserId) {
      throw new Error('Only the host can toggle demo mode');
    }

    // Reset ready approvals when demo mode changes (but keep host as ready)
    await updateDoc(doc(db, this.LOBBIES_COLLECTION, lobbyId), {
      isDemo,
      readyApprovals: {
        [lobby.hostUserId]: true, // Host remains ready
      },
      status: 'configuring',
    });

    console.log(`🔧 Demo mode ${isDemo ? 'enabled' : 'disabled'} for lobby ${lobbyId}`);
  }

  /**
   * Approve settings (ready check)
   */
  async approveSettings(lobbyId: string, userId: string): Promise<void> {
    // Run as a transaction so concurrent ready-ups merge instead of
    // clobbering each other. The previous read-modify-write off a plain
    // getDoc snapshot could leave readyApprovals={all:true} while status
    // stayed 'configuring' when two players readied at the same moment.
    const lobbyRef = doc(db, this.LOBBIES_COLLECTION, lobbyId);
    await runTransaction(db, async (tx) => {
      const lobbyDoc = await tx.get(lobbyRef);
      if (!lobbyDoc.exists()) {
        throw new Error('Lobby not found');
      }
      const data = lobbyDoc.data() as any;

      const participants: any[] = data.participants || [];
      const userParticipant = participants.find((p) => p.userId === userId);
      if (!userParticipant) {
        console.error('❌ User not in lobby:', { userId, lobbyId, participants });
        throw new Error('You are not in this lobby');
      }

      if (userParticipant.status !== 'joined') {
        console.error('❌ User not joined:', {
          userId,
          lobbyId,
          userStatus: userParticipant.status,
          participantData: userParticipant,
        });
        throw new Error(`Cannot ready up - your status is '${userParticipant.status}'. Please refresh the page.`);
      }

      const updatedApprovals = {
        ...(data.readyApprovals || {}),
        [userId]: true,
      };

      // Check if all joined participants have approved
      const joinedParticipants = participants.filter((p) => p.status === 'joined');
      const allReady = joinedParticipants.every((p) => updatedApprovals[p.userId]);

      console.log('✅ User ready:', {
        userId,
        lobbyId,
        joinedParticipants: joinedParticipants.length,
        approvals: updatedApprovals,
        allReady,
        newStatus: allReady ? 'ready' : 'configuring',
      });

      tx.update(lobbyRef, {
        readyApprovals: updatedApprovals,
        status: allReady ? 'ready' : 'configuring',
      });
    });
  }

  /**
   * Unapprove settings (cancel ready)
   */
  async unapproveSettings(lobbyId: string, userId: string): Promise<void> {
    const lobby = await this.getLobby(lobbyId);
    if (!lobby) {
      throw new Error('Lobby not found');
    }

    const updatedApprovals = {
      ...lobby.readyApprovals,
      [userId]: false,
    };

    await updateDoc(doc(db, this.LOBBIES_COLLECTION, lobbyId), {
      readyApprovals: updatedApprovals,
      status: 'configuring',
    });
  }

  /**
   * Delete lobby (host only)
   */
  async deleteLobby(lobbyId: string, userId: string): Promise<void> {
    const lobby = await this.getLobby(lobbyId);
    if (!lobby) {
      throw new Error('Lobby not found');
    }

    if (userId !== lobby.hostUserId) {
      throw new Error('Only the host can delete the lobby');
    }

    await deleteDoc(doc(db, this.LOBBIES_COLLECTION, lobbyId));
  }

  /**
   * Get all public lobbies (Quick Match only, waiting status)
   */
  async getPublicLobbies(): Promise<TournamentLobby[]> {
    const lobbiesRef = collection(db, this.LOBBIES_COLLECTION);
    const q = query(
      lobbiesRef,
      where('isPersistent', '==', false),
      where('status', '==', 'waiting')
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        hostUserId: data.hostUserId,
        groupId: data.groupId,
        isPersistent: data.isPersistent,
        status: data.status,
        tournamentId: data.tournamentId,
        createdAt: data.createdAt.toDate(),
        participants: data.participants.map((p: any) => ({
          userId: p.userId,
          displayName: p.displayName,
          photoURL: p.photoURL,
          customAvatarType: p.customAvatarType,
          customAvatarValue: p.customAvatarValue,
          avatarBackgroundType: p.avatarBackgroundType,
          avatarBackgroundValue: p.avatarBackgroundValue,
          status: p.status,
          joinedAt: p.joinedAt ? p.joinedAt.toDate() : undefined,
        })),
        settings: data.settings,
        readyApprovals: data.readyApprovals || {},
      } as TournamentLobby;
    });
  }

  /**
   * Get lobbies where user is invited (status: 'invited')
   */
  async getPendingInvitations(userId: string): Promise<TournamentLobby[]> {
    const lobbiesRef = collection(db, this.LOBBIES_COLLECTION);
    // Get all active lobbies (not finished)
    const q = query(
      lobbiesRef,
      where('status', 'in', ['waiting', 'configuring', 'ready'])
    );
    const snapshot = await getDocs(q);

    const invitedLobbies: TournamentLobby[] = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      // Check if user is invited (not joined)
      const userParticipant = data.participants.find((p: any) => p.userId === userId);
      if (userParticipant && userParticipant.status === 'invited') {
        invitedLobbies.push({
          id: doc.id,
          hostUserId: data.hostUserId,
          groupId: data.groupId,
          isPersistent: data.isPersistent,
          status: data.status,
          tournamentId: data.tournamentId,
          createdAt: data.createdAt.toDate(),
          participants: data.participants.map((p: any) => ({
            userId: p.userId,
            displayName: p.displayName,
            photoURL: p.photoURL,
            customAvatarType: p.customAvatarType,
            customAvatarValue: p.customAvatarValue,
            avatarBackgroundType: p.avatarBackgroundType,
            avatarBackgroundValue: p.avatarBackgroundValue,
            status: p.status,
            joinedAt: p.joinedAt ? p.joinedAt.toDate() : undefined,
          })),
          settings: data.settings,
          readyApprovals: data.readyApprovals || {},
        });
      }
    });

    return invitedLobbies;
  }

  /**
   * Get lobbies where user is the host
   */
  async getMyLobbies(userId: string): Promise<TournamentLobby[]> {
    const lobbiesRef = collection(db, this.LOBBIES_COLLECTION);
    // Get all active lobbies where user is host
    const q = query(
      lobbiesRef,
      where('hostUserId', '==', userId),
      where('status', 'in', ['waiting', 'configuring', 'ready'])
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        hostUserId: data.hostUserId,
        groupId: data.groupId,
        isPersistent: data.isPersistent,
        status: data.status,
        createdAt: data.createdAt.toDate(),
        participants: data.participants.map((p: any) => ({
          userId: p.userId,
          displayName: p.displayName,
          photoURL: p.photoURL,
          customAvatarType: p.customAvatarType,
          customAvatarValue: p.customAvatarValue,
          avatarBackgroundType: p.avatarBackgroundType,
          avatarBackgroundValue: p.avatarBackgroundValue,
          status: p.status,
          joinedAt: p.joinedAt ? p.joinedAt.toDate() : undefined,
        })),
        settings: data.settings,
        readyApprovals: data.readyApprovals || {},
      } as TournamentLobby;
    });
  }

  /**
   * Get lobbies where user has joined (but is not host)
   */
  async getJoinedLobbies(userId: string): Promise<TournamentLobby[]> {
    const lobbiesRef = collection(db, this.LOBBIES_COLLECTION);
    // Get all active lobbies
    const q = query(
      lobbiesRef,
      where('status', 'in', ['waiting', 'configuring', 'ready'])
    );
    const snapshot = await getDocs(q);

    const joinedLobbies: TournamentLobby[] = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      // Check if user is joined (not invited) and is not the host
      const userParticipant = data.participants.find((p: any) => p.userId === userId);
      if (userParticipant && userParticipant.status === 'joined' && data.hostUserId !== userId) {
        joinedLobbies.push({
          id: doc.id,
          hostUserId: data.hostUserId,
          groupId: data.groupId,
          isPersistent: data.isPersistent,
          status: data.status,
          tournamentId: data.tournamentId,
          createdAt: data.createdAt.toDate(),
          participants: data.participants.map((p: any) => ({
            userId: p.userId,
            displayName: p.displayName,
            photoURL: p.photoURL,
            customAvatarType: p.customAvatarType,
            customAvatarValue: p.customAvatarValue,
            avatarBackgroundType: p.avatarBackgroundType,
            avatarBackgroundValue: p.avatarBackgroundValue,
            status: p.status,
            joinedAt: p.joinedAt ? p.joinedAt.toDate() : undefined,
          })),
          settings: data.settings,
          readyApprovals: data.readyApprovals || {},
        });
      }
    });

    return joinedLobbies;
  }

  /**
   * Subscribe to lobbies where the user is invited (real-time)
   */
  subscribeToMyInvitations(
    userId: string,
    callback: (invitations: TournamentLobby[]) => void
  ): () => void {
    const q = query(
      collection(db, this.LOBBIES_COLLECTION),
      where('status', 'in', ['waiting', 'configuring', 'ready'])
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const invitations: TournamentLobby[] = [];

        snapshot.docs.forEach((docSnapshot) => {
          const data = docSnapshot.data();
          
          // Check if user is an invited participant (not host, not joined)
          const userParticipant = data.participants?.find(
            (p: any) => p.userId === userId && p.status === 'invited'
          );

          if (userParticipant && data.hostUserId !== userId) {
            const lobby: TournamentLobby = {
              id: docSnapshot.id,
              hostUserId: data.hostUserId,
              groupId: data.groupId,
              isPersistent: data.isPersistent,
              status: data.status,
              tournamentId: data.tournamentId,
              createdAt: data.createdAt.toDate(),
              participants: data.participants.map((p: any) => ({
                userId: p.userId,
                displayName: p.displayName,
                photoURL: p.photoURL,
                customAvatarType: p.customAvatarType,
                customAvatarValue: p.customAvatarValue,
                avatarBackgroundType: p.avatarBackgroundType,
                avatarBackgroundValue: p.avatarBackgroundValue,
                status: p.status,
                joinedAt: p.joinedAt?.toDate(),
              })),
              settings: data.settings,
              readyApprovals: data.readyApprovals || {},
            };
            invitations.push(lobby);
          }
        });

        callback(invitations);
      },
      (error) => {
        console.error('Error subscribing to invitations:', error);
        callback([]);
      }
    );

    return unsubscribe;
  }

  /**
   * Close/delete a lobby (host only)
   */
  async closeLobby(lobbyId: string, userId: string): Promise<void> {
    const lobby = await this.getLobby(lobbyId);
    if (!lobby) {
      throw new Error('Lobby not found');
    }

    if (lobby.hostUserId !== userId) {
      throw new Error('Only the host can close the lobby');
    }

    await deleteDoc(doc(db, this.LOBBIES_COLLECTION, lobbyId));
  }

  /**
   * Submit a setting suggestion (non-host players)
   */
  async submitSuggestion(
    lobbyId: string,
    userId: string,
    displayName: string,
    settingKey: string,
    suggestedValue: any,
    currentValue: any
  ): Promise<void> {
    const lobby = await this.getLobby(lobbyId);
    if (!lobby) {
      throw new Error('Lobby not found');
    }

    // Host cannot suggest to themselves
    if (userId === lobby.hostUserId) {
      throw new Error('Host can directly change settings');
    }

    // Check if user is a participant
    const userParticipant = lobby.participants.find((p) => p.userId === userId);
    if (!userParticipant || userParticipant.status !== 'joined') {
      throw new Error('You must be in the lobby to suggest settings');
    }

    const suggestion = {
      id: `${userId}-${settingKey}-${Date.now()}`,
      lobbyId,
      fromUserId: userId,
      fromDisplayName: displayName,
      settingKey,
      suggestedValue,
      currentValue,
      createdAt: new Date(),
      status: 'pending',
    };

    // Add to suggestions array (or create if doesn't exist)
    const existingSuggestions = lobby.settingSuggestions || [];
    
    // Remove any existing suggestion from same user for same setting
    const filteredSuggestions = existingSuggestions.filter(
      (s) => !(s.fromUserId === userId && s.settingKey === settingKey)
    );

    await updateDoc(doc(db, this.LOBBIES_COLLECTION, lobbyId), {
      settingSuggestions: [...filteredSuggestions, suggestion],
    });

    console.log('💡 Setting suggestion submitted:', suggestion);
  }

  /**
   * Accept a suggestion (host only)
   */
  async acceptSuggestion(
    lobbyId: string,
    hostUserId: string,
    suggestionId: string
  ): Promise<void> {
    const lobby = await this.getLobby(lobbyId);
    if (!lobby) {
      throw new Error('Lobby not found');
    }

    if (hostUserId !== lobby.hostUserId) {
      throw new Error('Only the host can accept suggestions');
    }

    const suggestions = lobby.settingSuggestions || [];
    const suggestion = suggestions.find((s) => s.id === suggestionId);
    
    if (!suggestion) {
      throw new Error('Suggestion not found');
    }

    // Apply the suggested setting
    let settingsUpdate: any = {};
    if (suggestion.settingKey.startsWith('levelConstraints.')) {
      const constraintKey = suggestion.settingKey.split('.')[1];
      settingsUpdate = {
        levelConstraints: {
          ...lobby.settings.levelConstraints,
          [constraintKey]: suggestion.suggestedValue,
        },
      };
    } else {
      settingsUpdate = { [suggestion.settingKey]: suggestion.suggestedValue };
    }

    // Update settings and mark suggestion as accepted
    const updatedSuggestions = suggestions.map((s) =>
      s.id === suggestionId ? { ...s, status: 'accepted' } : s
    );

    await updateDoc(doc(db, this.LOBBIES_COLLECTION, lobbyId), {
      settings: {
        ...lobby.settings,
        ...settingsUpdate,
      },
      settingSuggestions: updatedSuggestions,
      readyApprovals: {
        [lobby.hostUserId]: true, // Host remains ready
      },
      status: 'configuring', // Reset ready status since settings changed
    });

    console.log('✅ Suggestion accepted:', suggestionId);
  }

  /**
   * Reject a suggestion (host only)
   */
  async rejectSuggestion(
    lobbyId: string,
    hostUserId: string,
    suggestionId: string
  ): Promise<void> {
    const lobby = await this.getLobby(lobbyId);
    if (!lobby) {
      throw new Error('Lobby not found');
    }

    if (hostUserId !== lobby.hostUserId) {
      throw new Error('Only the host can reject suggestions');
    }

    const suggestions = lobby.settingSuggestions || [];
    
    // Remove the suggestion
    const updatedSuggestions = suggestions.filter((s) => s.id !== suggestionId);

    await updateDoc(doc(db, this.LOBBIES_COLLECTION, lobbyId), {
      settingSuggestions: updatedSuggestions,
    });

    console.log('❌ Suggestion rejected:', suggestionId);
  }

  /**
   * Clear all pending suggestions (host only)
   */
  async clearSuggestions(lobbyId: string, hostUserId: string): Promise<void> {
    const lobby = await this.getLobby(lobbyId);
    if (!lobby) {
      throw new Error('Lobby not found');
    }

    if (hostUserId !== lobby.hostUserId) {
      throw new Error('Only the host can clear suggestions');
    }

    await updateDoc(doc(db, this.LOBBIES_COLLECTION, lobbyId), {
      settingSuggestions: [],
    });
  }
}

export const tournamentLobbyService = new TournamentLobbyService();

